import { lazy, Fragment, Suspense, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay/legacy';
import get from 'lodash.get';

import {
    getNewBrowserWindowOptions,
    DESIGN_MANAGER_PRO,
    DESIGN_MANAGER_STANDARD,
} from './tradeDesignManagerHelpers';

const TradeDesignManagerItemCaptureModal = lazy(() =>
    import(
        /* webpackChunkName: "TradeDesignManagerItemCaptureModal" */ './TradeDesignManagerItemCaptureModal'
    )
);

const TradeDesignManagerUpgradeRequestModal = lazy(() =>
    import(
        /* webpackChunkName: "TradeDesignManagerUpgradeRequestModal" */ './TradeDesignManagerUpgradeRequest/TradeDesignManagerUpgradeRequestModal'
    )
);

export function TradeDesignManagerModalComponent({ item, user, skuId, isOpen, onClose }) {
    const [isConnected, setIsConnected] = useState(
        get(user, 'socialNetworks[0].status') === 'CONNECTED'
    );

    const itemId = get(item, 'serviceId');

    const hasDesignManagerAccount = get(user, 'tradeFirmDesignManagerProfile.hasAccount');
    const designManagerAccountType = get(user, 'tradeFirmDesignManagerProfile.accountType');
    const isProAccount = designManagerAccountType === DESIGN_MANAGER_PRO;
    const isStandardAccount = designManagerAccountType === DESIGN_MANAGER_STANDARD;
    const showFeature = hasDesignManagerAccount && (isProAccount || isStandardAccount);

    useEffect(() => {
        if (showFeature && isOpen && !isConnected && isProAccount) {
            window.open('', 'design-manager-auth', getNewBrowserWindowOptions());
        }
    }, [isConnected, isOpen, isProAccount, showFeature]);

    if (!showFeature) {
        return null;
    }

    const modalToDisplay = isProAccount ? (
        <TradeDesignManagerItemCaptureModal
            isOpen={isOpen}
            itemId={itemId}
            skuId={skuId}
            onClose={onClose}
            onConnect={() => setIsConnected(true)}
        />
    ) : (
        <TradeDesignManagerUpgradeRequestModal
            isOpen={isOpen}
            onClose={onClose}
            userId={get(user, 'serviceId')}
        />
    );

    return (
        <Fragment>
            <Suspense fallback={null}>{modalToDisplay}</Suspense>
        </Fragment>
    );
}

TradeDesignManagerModalComponent.propTypes = {
    item: PropTypes.object.isRequired,
    user: PropTypes.object.isRequired,
    skuId: PropTypes.string.isRequired,
    isOpen: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
};

export const TradeDesignManagerModal = createFragmentContainer(TradeDesignManagerModalComponent, {
    item: graphql`
        fragment TradeDesignManagerModal_item on Item {
            serviceId
        }
    `,
    user: graphql`
        fragment TradeDesignManagerModal_user on User {
            serviceId
            tradeFirmDesignManagerProfile {
                hasAccount
                accountType
            }
            socialNetworks(socialNetworkNames: DESIGN_MANAGER) {
                ... on DesignManagerSocialNetworkType {
                    status
                }
            }
        }
    `,
});
